import { MapPin } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { DashboardBridge } from '../Dashboard/DashboardBridge'
import { formatPosition } from '../DashboardElements/fleet-robot-details'
import { hasCoords } from './alert-log'

/** Same scale a fleet-map marker click focuses to (`useFleetMarkers` FOCUS_ZOOM), so the two agree. */
export const SHOW_ON_MAP_ZOOM = 15

interface LogLocationProps {
  lng?: number
  lat?: number
  /** Null outside a dashboard (the header-bell drawer): coordinates still print, the button does not. */
  bridge: DashboardBridge | null
  /**
   * True on an occurrence row that is not the newest. Only the presence's LATEST fix is stored, so
   * every row of one alarm shows the same place; the tooltip says so instead of implying a track.
   */
  latestOnly?: boolean
}

/** Where it happened, and a way to see it — the two things a log line adds beyond when and what. */
export function LogLocation({ lng, lat, bridge, latestOnly = false }: LogLocationProps) {
  const { t } = useTranslation('console')
  const coords = { lng, lat }

  if (!hasCoords(coords)) {
    return (
      <span className="inline-flex items-center gap-1">
        <MapPin className="h-3 w-3 shrink-0" aria-hidden />
        {t('alerts.log.noLocation', 'No location')}
      </span>
    )
  }

  const showOnMap = () => {
    // Pan and zoom ONLY. Selecting the robot here would re-scope every follow-selection element —
    // this very list included — and the rows the operator was reading would vanish under them.
    bridge?.emit('zoom-to-location', { center: [coords.lng, coords.lat], zoom: SHOW_ON_MAP_ZOOM })
  }

  return (
    <>
      <span
        className="inline-flex items-center gap-1 font-mono text-[11px]"
        title={
          latestOnly ? t('alerts.log.locationOfLatest', 'Location of the latest occurrence of this alarm') : undefined
        }
      >
        <MapPin className="h-3 w-3 shrink-0" aria-hidden />
        {formatPosition(coords.lat, coords.lng)}
      </span>
      {bridge && (
        <>
          <span aria-hidden>·</span>
          <button
            type="button"
            onClick={showOnMap}
            className="rounded font-medium text-blue-600 hover:underline focus-visible:ring-2 dark:text-blue-400"
          >
            {t('alerts.log.showOnMap', 'Show on map')}
          </button>
        </>
      )}
    </>
  )
}
